/**
 * myDayStore.js
 *
 * AsyncStorage persistence of the daily "Mai napom" briefing state, per user (and per local day where it applies).
 * Every function is best-effort and never throws — a storage failure must never block the app.
 *
 * @module helpers/myDayStore
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import {hideKey, nextLaunchKey, openedKey, snoozeKey} from './myDayBriefing';
import {autoOpenOptOutKey} from './salesFollowUpApi';

const safe = async (fn, fallback) => {
    try {
        return await fn();
    } catch (_e) {
        return fallback;
    }
};

const dayBefore = (day) => {
    const [y, m, d] = String(day).split('-').map(Number);
    const prev = new Date(y, (m || 1) - 1, (d || 1) - 1);
    const p = (n) => String(n).padStart(2, '0');

    return `${prev.getFullYear()}-${p(prev.getMonth() + 1)}-${p(prev.getDate())}`;
};

/**
 * Read everything the auto-open decision needs.
 * @param {string} userId user id. @param {string} day local day key.
 * @returns {Promise<{opened: boolean, hidden: boolean, optedOut: boolean, snoozeUntil: number|null, nextLaunch: boolean}>} state.
 */
export const loadMyDayState = (userId, day) => safe(async () => {
    const [opened, hidden, optedOut, snooze, next] = await Promise.all([
        AsyncStorage.getItem(openedKey(userId, day)),
        AsyncStorage.getItem(hideKey(userId, day)),
        AsyncStorage.getItem(autoOpenOptOutKey(userId, day)),
        AsyncStorage.getItem(snoozeKey(userId)),
        AsyncStorage.getItem(nextLaunchKey(userId)),
    ]);
    const until = Number(snooze);

    return {
        opened: opened === '1',
        hidden: hidden === '1',
        optedOut: optedOut === '1',
        snoozeUntil: Number.isFinite(until) && until > 0 ? until : null,
        nextLaunch: next === '1',
    };
}, {opened: false, hidden: false, optedOut: false, snoozeUntil: null, nextLaunch: false});

/**
 * The briefing was shown: mark today seen, drop any pending snooze / next-launch flag, prune yesterday's keys.
 * @param {string} userId user id. @param {string} day local day key. @returns {Promise<void>}
 */
export const markOpened = (userId, day) => safe(async () => {
    const yesterday = dayBefore(day);

    await Promise.all([
        AsyncStorage.setItem(openedKey(userId, day), '1'),
        AsyncStorage.removeItem(snoozeKey(userId)),
        AsyncStorage.removeItem(nextLaunchKey(userId)),
        AsyncStorage.removeItem(openedKey(userId, yesterday)),
        AsyncStorage.removeItem(hideKey(userId, yesterday)),
    ]);
}, undefined);

/**
 * "Értesítsen újra": remember when to reopen (epoch ms).
 * @param {string} userId user id. @param {number} dueAt epoch ms. @returns {Promise<void>}
 */
export const saveSnooze = (userId, dueAt) => safe(() => AsyncStorage.setItem(snoozeKey(userId), String(dueAt)), undefined);

/**
 * "Következő induláskor": reopen on the next cold start / long foreground return.
 * @param {string} userId user id. @returns {Promise<void>}
 */
export const saveNextLaunch = (userId) => safe(() => AsyncStorage.setItem(nextLaunchKey(userId), '1'), undefined);

/**
 * "Kihagyás ma": no auto-open for the rest of the local day; clears snooze + next-launch.
 * @param {string} userId user id. @param {string} day local day key. @returns {Promise<void>}
 */
export const saveHidden = (userId, day) => safe(async () => {
    await Promise.all([
        AsyncStorage.setItem(hideKey(userId, day), '1'),
        AsyncStorage.removeItem(snoozeKey(userId)),
        AsyncStorage.removeItem(nextLaunchKey(userId)),
    ]);
}, undefined);
